import { useToolboxQuery } from "@/api/toolbox";
import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import {
  useAppNavigate,
  usePaneLocation,
  usePaneParams,
} from "@/hooks/pane-routing";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import { entityWithEnrichedOpenTabUrl } from "@/panes/shared/entityWithEnrichedOpenTabUrl";
import type { GithubProject } from "@cohesive/api-entities";
import {
  type Entity,
  IntegrationIcon,
  Markdown,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { X, XCircle } from "lucide-react";
import { useCallback, useMemo } from "react";

// github-project-get returns the typed entity envelope; the project fields are
// nested under `.data`.
type ProjectEntityResponse = { data?: GithubProject };

export function GitHubProjectDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  usePaneParams(ROUTES.GITHUB.PROJECT_DETAIL);
  const paneLocation = usePaneLocation("rightPane");
  const entity = paneLocation?.state?.entity as Entity | undefined;
  const data = (entity?.data ?? {}) as Record<string, unknown>;

  // Projects arrive as generic entities; their ProjectV2 node id (PVT_...) is
  // what the project-get action needs.
  const nodeId = typeof data.id === "string" ? data.id : undefined;
  const connectionId = useIntegrationConnection("github")?.id;
  const canFetch = !!connectionId && !!nodeId && nodeId.startsWith("PVT_");

  const { data: projectResp, isLoading } =
    useToolboxQuery<ProjectEntityResponse>(
      canFetch
        ? {
            integrationConnectionId: connectionId,
            actionName: "github-project-get",
            actionInput: { project_id: nodeId },
          }
        : null,
      { staleTime: 5 * 60 * 1000 },
    );

  const project = projectResp?.data;

  // Prefer fetched values; fall back to the generic entity for an instant header.
  const title =
    project?.title ||
    project?.name ||
    (typeof data.title === "string" ? data.title : "Project");
  const description =
    project?.short_description ||
    project?.description ||
    (typeof data.description === "string" ? data.description : undefined);

  const entityForHeaderActions = useMemo(
    () =>
      entity
        ? entityWithEnrichedOpenTabUrl(entity, {
            url: data.url as string | undefined,
            web_link: project?.web_link,
          })
        : undefined,
    [entity, data.url, project?.web_link],
  );

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  if (!entity) {
    return (
      <div className="flex flex-col h-full bg-background items-center justify-center">
        <XCircle className="h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">Project not found</p>
      </div>
    );
  }

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="github"
              name="GitHub"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {title}
            </span>
          </div>
          <TooltipProvider>
            {entityForHeaderActions && (
              <EntityPaneHeaderObjectActions
                entity={entityForHeaderActions}
                name={title}
              />
            )}
            <PaneExpandButton />
            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent>
        <div className="space-y-5">
          {/* Title + metadata */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold">{title}</h2>
              {project?.number !== undefined && (
                <span className="text-sm text-muted-foreground">
                  #{project.number}
                </span>
              )}
              {project?.closed && (
                <Badge variant="outline" className="text-xs">
                  Closed
                </Badge>
              )}
              {project?.visibility && (
                <Badge variant="secondary" className="text-xs">
                  {project.visibility === "PUBLIC" ? "Public" : "Private"}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {project?.owner_login && <span>{project.owner_login}</span>}
              {project?.updated_at && (
                <span>
                  Updated{" "}
                  {formatDistanceToNow(new Date(project.updated_at), {
                    addSuffix: true,
                  })}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {description && (
            <p className="text-sm text-foreground">{description}</p>
          )}

          {/* README */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">README</h3>
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {isLoading ? (
                <p className="text-muted-foreground">Loading…</p>
              ) : project?.readme ? (
                <Markdown>{project.readme}</Markdown>
              ) : (
                <p className="text-muted-foreground italic">
                  No README provided
                </p>
              )}
            </div>
          </div>
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
